import PropTypes from "prop-types";
import React, { Component } from "react";
import { createPortal } from "react-dom";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import withRouter from "react-router-dom/withRouter";

import Banner from "../components/Banner";
import CookieMessageContainer from "../components/CookieMessage/container";
import LayoutHead from "../components/LayoutHead";
import LayoutNav from "../components/LayoutNav";
import ModalRootContainer from "../components/ModalRoot/container";
import transitionDirection from "../utils/transitionDirection";
import sharingConfig from "../utils/sharingConfig";

import "./index.css";
import styles from "./index.module.css";
import transitionStyles from "./transitions.module.css";

import { navigateTo } from "gatsby-link";

/* A masking element, uses React's Portals to allow a component to sit 'above'
 * the rest of the app. Used to mask out areas of the screen during animations.
 */
class Mask extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement("div");
  }

  componentDidMount() {
    // Append the element into the DOM on mount
    const maskRoot = document.getElementById("maskRoot");
    maskRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    // Remove the element from the DOM when we unmount
    const maskRoot = document.getElementById("maskRoot");
    maskRoot.removeChild(this.el);
  }

  render() {
    // Use a portal to render the children into the element
    return createPortal(
      // Any valid React child: JSX, strings, arrays, etc.
      this.props.children,
      // A DOM element
      this.el
    );
  }
}

class TransitionHandler extends React.Component {
  shouldComponentUpdate() {
    // if (this.props.location.pathname !== window.location.pathname) {
    //   console.log( "props.location - window.location", this.props.location.pathname, window.location.pathname );
    // }
    return this.props.location.pathname === window.location.pathname;
  }

  render() {
    return this.props.children;
  }
}

TransitionHandler.propTypes = {
  location: PropTypes.shape({ pathname: PropTypes.string })
};

/* Extract basic chapter data from an array of GraphQl edge objects */
const getChaptersFromEdges = edges => {
  return edges.map(edge => {
    return {
      path: edge.node.path,
      title: edge.node.title,
      titleNumber: edge.node.titleNumber
    };
  });
};

class Layout extends Component {
  constructor(props) {
    super(props);
    const chapterPaths = props.data.allSoetChapterNode.edges.map(edge => ({
      prevPath: edge.node.prevPath,
      nextPath: edge.node.nextPath,
      childPath: edge.node.childPath,
      path: edge.node.path,
      chapterId: edge.node.chapterId
    }));
    const articlePaths = props.data.allSoetArticleNode.edges.map(edge => ({
      prevPath: edge.node.prevPath,
      nextPath: edge.node.nextPath,
      parentPath: edge.node.parentPath,
      path: edge.node.path,
      chapterId: edge.node.chapterId
    }));

    // Setup paths used for kb / gesture navigation
    const navChapterPaths = [...chapterPaths];
    // nextPath for chapter is blank, so 'next chapter' button is not displayed
    // on chapter page.
    // We want to allow kb nav to next section, so add a nextpath for usage
    // in this copy of the paths
    navChapterPaths[navChapterPaths.length - 1].nextPath = "/saved/";
    const navPaths = [
      ...navChapterPaths,
      ...articlePaths,
      // add some hardcoded paths
      {
        path: "/",
        nextPath: chapterPaths[0].path
      },
      {
        prevPath: chapterPaths[chapterPaths.length - 1].path,
        path: "/saved/",
        nextPath: "/filter/"
      },
      {
        prevPath: "/saved/",
        path: "/filter/"
      }
    ];

    this.state = {
      navOpen: false,
      isArticleTransition: false,
      transitionNames: {
        enter: transitionStyles.downEnter,
        enterActive: transitionStyles.downEnterActive,
        exit: transitionStyles.downExit,
        exitActive: transitionStyles.downExitActive
      },
      chapterPaths,
      articlePaths,
      navPaths,
      chapters: getChaptersFromEdges(this.props.data.allSoetChapterNode.edges)
    };
    this.transitionChildFactory = this.transitionChildFactory.bind(this);
    this.getChildContext = this.getChildContext.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    window.addEventListener("keydown", this.handleKeyDown);
  }
  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  handleKeyDown(e) {
    // e.which is deprecated, e.key is not fully supported so check both
    const key = e.key;
    const keyCode = e.which;
    const isNavPress =
      ["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown"].includes(key) ||
      [37, 38, 39, 40].includes(keyCode);
    if (!isNavPress) return;

    const currLoc = this.props.location.pathname;
    const paths = this.state.navPaths;
    let matchedPath = paths.find(item => item.path === currLoc);
    if (!matchedPath) return;

    let navType = false;
    if (key === "ArrowLeft" || keyCode === 37) navType = "parentPath";
    if (key === "ArrowUp" || keyCode === 38) navType = "prevPath";
    if (key === "ArrowRight" || keyCode === 39) navType = "childPath";
    if (key === "ArrowDown" || keyCode === 40) navType = "nextPath";
    if (navType && matchedPath[navType]) navigateTo(matchedPath[navType]);
  }

  componentWillReceiveProps(nextProps) {
    const currLoc = this.props.location.pathname;
    const nextLoc = nextProps.location.pathname;
    if (currLoc === nextLoc) return;

    // Transition direction will change based on where in the site structure
    // the current and next locations point to.
    const { isArticleTransition, direction } = transitionDirection(
      currLoc,
      nextLoc,
      this.state.articlePaths,
      this.state.chapterPaths
    );
    this.setState({
      isArticleTransition,
      transitionNames: {
        enter: transitionStyles[`enter${direction}`],
        enterActive: transitionStyles[`enterActive${direction}`],
        exit: transitionStyles[`exit${direction}`],
        exitActive: transitionStyles[`exitActive${direction}`]
      }
    });
  }

  /**
   * Used by <TransitionGroup> to allow components scheduled for exit to have
   *  their transition styles changed.
   *
   * Full gory details available at: https://stackoverflow.com/questions/41404232/
   */
  transitionChildFactory(child) {
    return React.cloneElement(child, {
      classNames: this.state.transitionNames
    });
  }

  maskStyles = {
    position: "fixed",
    left: "60px",
    width: "360px",
    top: 0,
    bottom: 0,
    zIndex: 20
  };

  getChildContext() {
    return {
      getEl: Mask => {
        this.setState({
          MaskEl: React.cloneElement(Mask, {
            style: this.maskStyles
          })
        });
      }
    };
  }

  render() {
    const { children, location } = this.props;
    const { chapters, MaskEl, isArticleTransition } = this.state;
    const homeOverride = location.pathname === "/";
    const shareImage = sharingConfig.shareImage;
    const matchingArticlePaths = this.state.articlePaths.filter(path => {
      return path.path == this.props.location.pathname;
    });
    const isArticle = matchingArticlePaths.length > 0;
    const matchingChapterPath = this.state.chapterPaths.filter(path => {
      return path.path == this.props.location.pathname;
    });
    const isChapter = matchingChapterPath.length > 0;

    return (
      <div>
        <LayoutHead>
          <meta property="og:image" content={shareImage} />
          <meta property="og:type" content="website" />
          <meta name="og:title" content="The State of European Tech 2017" />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content="@atomico" />
          <meta name="twitter:creator" content="@atomico" />
          <meta
            name="twitter:title"
            content="The State of European Tech 2017"
          />
          <meta name="twitter:image" content={shareImage} />
          {!isArticle &&
            !isChapter && (
              <meta
                name="description"
                content="The State of European Tech 2017 is the deepest, data-led investigation into the European tech ecosystem."
              />
            )}
          {!isArticle &&
            !isChapter && (
              <meta
                name="twitter:description"
                content="The State of European Tech 2017 is the deepest, data-led investigation into the European tech ecosystem - read it here."
              />
            )}
          {!isArticle &&
            !isChapter && (
              <meta
                name="og:description"
                content="The State of European Tech 2017 is the deepest, data-led investigation into the European tech ecosystem"
              />
            )}
        </LayoutHead>
        <div className={styles.shell}>
          <LayoutNav chapters={chapters} currentPath={location.pathname} />
          <TransitionGroup
            className={styles.content}
            childFactory={this.transitionChildFactory}
          >
            <CSSTransition
              // Use location.key as it's always unique, even for identical pathnames
              // Using location.pathname can cause transitions to break when repeatedly
              // toggling between two screens
              key={location.key}
              timeout={{ enter: 650, exit: 650 }}
            >
              {status => {
                return (
                  <div
                    className={styles.transitionHandler}
                    style={homeOverride ? { overflowY: "scroll" } : {}}
                  >
                    {status === "entering" &&
                      isArticleTransition && <Mask>{MaskEl}</Mask>}
                    <TransitionHandler location={location}>
                      {children()}
                    </TransitionHandler>
                  </div>
                );
              }}
            </CSSTransition>
          </TransitionGroup>
        </div>
        <ModalRootContainer />
        <CookieMessageContainer />
        {homeOverride && <Banner />}
        <div id="maskRoot" />
        <div id="buttonRoot" />
      </div>
    );
  }
}

Layout.childContextTypes = {
  getEl: PropTypes.func
};

Layout.propTypes = {
  children: PropTypes.func,
  location: PropTypes.shape({ pathname: PropTypes.string }),
  data: PropTypes.shape({
    allSoetChapterNode: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    allSoetArticleNode: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({}))
    })
  })
};

export default withRouter(Layout);

// eslint-disable-next-line no-undef
export const layoutQuery = graphql`
  query LayoutQuery {
    allSoetChapterNode(sort: { fields: [sortOrder], order: ASC }) {
      edges {
        node {
          chapterId
          slug
          prevPath
          path
          nextPath
          childPath
          title
          titleNumber
        }
      }
    }
    allSoetArticleNode(sort: { fields: [sortOrder], order: ASC }) {
      edges {
        node {
          prevPath
          path
          nextPath
          parentPath
          chapterId
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/layouts/index.js